'use client';

import type { SearchIntent } from '@/lib/smart-finder/types';

export const SEARCH_METHOD_OPTIONS: Array<{
  value: SearchIntent;
  label: string;
  description: string;
}> = [
  {
    value: 'cnr',
    label: 'CNR number',
    description: 'Use the 16-character code on your case record.',
  },
  {
    value: 'case_number',
    label: 'Case number',
    description: 'Use the case type, number and filing year.',
  },
  {
    value: 'party_name',
    label: 'Party name',
    description: 'No case number? Use a person or company name.',
  },
];

interface SearchMethodSelectorProps {
  value: SearchIntent;
  onChange: (value: SearchIntent) => void;
  modes: { caseNumber: boolean; partyName: boolean };
  disabled?: boolean;
}

export default function SearchMethodSelector({
  value,
  onChange,
  modes,
  disabled = false,
}: SearchMethodSelectorProps) {
  const visibleOptions = SEARCH_METHOD_OPTIONS.filter((option) => (
    option.value === 'cnr'
    || (option.value === 'case_number' && modes.caseNumber)
    || (option.value === 'party_name' && modes.partyName)
  ));

  return (
    <fieldset className="mb-4" disabled={disabled}>
      <legend className="text-sm font-bold text-gray-900">Choose how to search your case</legend>
      <p className="mt-0.5 text-xs leading-5 text-gray-600">Select one option below. You can change it anytime.</p>
      <div className={`mt-2 grid gap-2 ${visibleOptions.length === 3 ? 'sm:grid-cols-3' : visibleOptions.length === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {visibleOptions.map((option) => {
          const selected = value === option.value;
          const descriptionId = `search-method-${option.value}-description`;

          return (
            <label
              key={option.value}
              className={`flex min-h-16 cursor-pointer items-start gap-3 rounded-xl border-2 px-3 py-3 text-left transition-colors ${
                selected
                  ? 'border-primary bg-primary-50 shadow-sm ring-1 ring-primary/10'
                  : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/50'
              } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <input
                type="radio"
                name="case-search-method"
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                aria-describedby={descriptionId}
                className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
              />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={`whitespace-nowrap text-sm font-bold ${selected ? 'text-primary' : 'text-gray-900'}`}>
                    {option.label}
                  </span>
                  {selected && <span className="text-[11px] font-bold uppercase tracking-wide text-primary">Selected</span>}
                </span>
                <span id={descriptionId} className="mt-0.5 block text-xs leading-4 text-gray-600">
                  {option.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
